/**
 * dashboardView.ts — pure view logic for the Sales Dashboard (P10 parity).
 * Kept component-free so vitest pins the behaviour (the closeFlow/leadsFlow
 * pattern).
 */
import type { SalesDeal, DealStage } from '../../types'

// The four OPEN stages (mockup pipeline order). Closed and Unknown are out.
const OPEN_STAGES: ReadonlySet<DealStage> = new Set<DealStage>([
  'New Lead', 'Qualified', 'Proposal Sent', 'Negotiation',
])

/**
 * Top open deals by value, mockup card shape (§10.F: 4 rows).
 *
 * HONEST CEILING: the input is the /v1/sales/deals list call, which is
 * capped at limit=200 newest-first (module.go ORDER BY created_at DESC) —
 * so this is "top of the newest 200", not a server-side global sort. The
 * D4-3 fix, if the cap ever bites, is a server sort param — not a bigger
 * client fetch.
 */
export function topOpenDeals(deals: SalesDeal[], n = 4): SalesDeal[] {
  // Ranked on the GBP-COMPARABLE leg (0111): original-currency pence are
  // not comparable across rows. An uncaptured row (basePence null — only
  // raw history could) has no honest rank and sorts last.
  return deals
    .filter((d) => OPEN_STAGES.has(d.stage))
    .sort((a, b) => (b.basePence ?? -1) - (a.basePence ?? -1))
    .slice(0, n)
}

/**
 * "Closed this month" in pence: the CURRENT month's closed-won value.
 * revenueByMonth is the server's zero-filled 6-bucket window, ascending
 * (ORDER BY m.month), so the last bucket IS the current month — no client
 * date math, no timezone re-derivation.
 */
export function closedThisMonthPence(monthlyRevenue: { month: string; won: number }[]): number {
  return monthlyRevenue.length > 0 ? monthlyRevenue[monthlyRevenue.length - 1].won : 0
}
